<script lang="ts">
    import type { GlobalStorage } from '@otto-ec/global-resources/storage';
    import type { OcSwitchV1Events } from '@otto-ec/otto-components/switch';
    import { onMount } from 'svelte';
    import { useComparisonContext } from '@shared/providers/comparison-context/ComparisonContext.browser';
    import { injectWiring } from '@tilelist/application/wiring/TileListWiringContext.svelte.ts';

    const wiring = injectWiring(['trackingService']);

    const context = useComparisonContext();
    const BANNER_CLOSED_KEY = 'reptile_banner_closed';
    const SHOW_COMPARISON_KEY = 'reptile_show_comparison';

    let bannerClosed = $state(true);
    let sessionStorage: GlobalStorage | null = null;

    function onSwitchChange({ detail }: OcSwitchV1Events['oc-property-change']) {
        if (detail.checked?.current !== undefined) {
            context.showComparison = detail.checked.current;
        }
        wiring.trackingService?.trackComparisonActivity(detail.checked?.current ? 'toggle_on' : 'toggle_off');
    }

    function onCloseBanner() {
        if (!sessionStorage) return;
        sessionStorage.setItem(BANNER_CLOSED_KEY, 'true');
        bannerClosed = true;
    }

    const label = $derived(
        !context.showComparison
            ? 'Du kannst dich nicht entscheiden? Nutze unseren Produktvergleich und füge Produkte dem Vergleich hinzu.'
            : 'Produktvergleich aktiviert. Füge jetzt Produkte von dieser Liste hinzu.',
    );

    let restoringFromCache = false;

    $effect(() => {
        const showComparison = context.showComparison ? 'true' : 'false';
        if (restoringFromCache) {
            restoringFromCache = false;
            return;
        }
        if (sessionStorage) {
            sessionStorage.setItem(SHOW_COMPARISON_KEY, showComparison);
        }
    });

    onMount(async () => {
        sessionStorage = (await import('@otto-ec/global-resources/storage')).storage(window.sessionStorage);
        bannerClosed = sessionStorage.getItem(BANNER_CLOSED_KEY) === 'true';

        const cachedShowComparison = sessionStorage.getItem(SHOW_COMPARISON_KEY) === 'true';
        if (cachedShowComparison !== context.showComparison) {
            restoringFromCache = true;
            context.showComparison = cachedShowComparison;
        }
    });
</script>

{#if context.initialized && !bannerClosed}
    <oc-banner-v1
        hide-icon
        variant="hint"
        class="span-grid"
        onoc-banner-close={onCloseBanner}
        data-qa="reptile-product-comparison-banner"
    >
        <oc-switch-v2
            size={'50'}
            fit-content
            checked={context.showComparison}
            onoc-property-change={onSwitchChange}
            data-qa="reptile-product-comparison-toggle"
        >
            {label}
        </oc-switch-v2>
    </oc-banner-v1>
{/if}

<style>
    .span-grid {
        grid-column: 1/-1;
    }
</style>
